<template id="pagination-template">
  <div class="al-pagination_container" test-id="al-pagination">
    <ul v-if="count > 0" class="al-pagination pagination">
      <li v-if="pageCount > 8" class="al-pagination_prev" :class="{ disabled: isCurrent(1) }">
        <a tabindex="-1" href="javascript:;" :title="'Previous page'" @click="previousPage">
          <al-icon icon="chevron-left" />
        </a>
      </li>

      <li v-if="limitedPages[0] > 1" :class="{ active: isCurrent(1) }">
        <a href="javascript:;" tabindex="-1" @click="setPage(1)">{{ 1 }}</a>
      </li>
      <li v-if="limitedPages[0] > 2" class="disabled">
        <al-contextual-menu page position="bottom">
          <div class="page-number">
            <b-form-input
              v-model="pageEntered"
              class="input-small"
              size="xs"
              type="number"
              no-wheel
              @change="setPage(pageEntered)"
            ></b-form-input>
          </div>
        </al-contextual-menu>
      </li>

      <li v-for="page in limitedPages" :key="page" :class="{ active: isCurrent(page) }">
        <a href="javascript:;" tabindex="-1" @click="setPage(page)">{{ page }}</a>
      </li>

      <li v-if="limitedPages[limitedPages.length - 1] < pageCount - 1" class="disabled">
        <div class="contextual-menu">
          <al-contextual-menu page position="bottom">
            <div class="page-number">
              <b-form-input
                v-model="pageEntered"
                class="input-small"
                size="xs"
                type="number"
                no-wheel
                @change="setPage(pageEntered)"
              ></b-form-input>
            </div>
          </al-contextual-menu>
        </div>
      </li>
      <li
        v-if="limitedPages[limitedPages.length - 1] < pageCount"
        :class="{ active: isCurrent(pageCount) }"
      >
        <a href="javascript:;" tabindex="-1" @click="setPage(pageCount)">{{ pageCount }}</a>
      </li>

      <li
        v-if="pageCount > 8"
        class="al-pagination_next"
        :class="{ disabled: isCurrent(pageCount) }"
      >
        <a tabindex="-1" href="javascript:;" @click="nextPage">
          <al-icon icon="chevron-right" />
        </a>
      </li>
    </ul>
    <div class="al-pagination_count">
      <slot>
        <div v-if="count > 0 && showItemsPerPage">
          <al-select
            :value="itemsPerPageAsSelect"
            size="sm"
            label="displayed"
            :options="selectPageOptions"
            width="204px"
            placeholder="items per page"
            @select="onPageSizeChange"
          />
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import AlContextualMenu from '../contextual-menu/AlContextualMenu.vue';
import AlIcon from '../inline/icon/AlIcon.vue';
import AlSelect from '../form-components/select/AlSelect.vue';

export default {
  name: 'ComponentPagination',
  delimiters: ['${', '}'],
  components: {
    AlContextualMenu,
    AlIcon,
    AlSelect
  },
  props: {
    itemsPerPageOptions: { type: Array, default: () => [10, 25, 50, 100] },
    itemsPerPage: { type: Number, default: 10 },
    showItemsPerPage: { type: Boolean, default: true },

    pageCount: { type: Number, default: 0 },
    count: { type: Number, default: 0 },
    perPage: { type: Number, default: 0 },

    currentPage: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      pageEntered: null
    };
  },
  computed: {
    itemsPerPageAsSelect: {
      get() {
        return (
          this.selectPageOptions.find((item) => item.id === this.itemsPerPage) ||
          this.selectPageOptions[0]
        );
      }
    },
    selectPageOptions() {
      return this.itemsPerPageOptions.map((value) => ({
        id: value,
        name: `${value}`,
        displayed: `Items per page: ${value}`
      }));
    },
    firstInPage() {
      return this.perPage * (this.currentPage - 1) + 1;
    },
    lastInPage() {
      const max = this.perPage * this.currentPage;
      return this.count < max ? this.count : max;
    },
    // TODO: we need to change the logic of this compute to avoid mutating props inside
    limitedPages() {
      const chunkPagesLength = 4;
      const pages = Array.from({ length: this.pageCount + 1 })
        .map(Number.call, Number)
        .slice(1);
      const leftLimit = this.currentPage - Math.ceil(chunkPagesLength / 2) - 1;
      const rightLimit = this.currentPage + Math.floor(chunkPagesLength / 2);
      let start = Math.max(0, leftLimit);
      let end = Math.min(rightLimit, this.pageCount);
      if (leftLimit < 0) {
        end -= leftLimit;
      }
      if (rightLimit > this.pageCount) {
        start = Math.max(0, start - (rightLimit - this.pageCount));
      }
      return pages.slice(start, end);
    }
  },

  methods: {
    onPageSizeChange(value) {
      this.$emit('update:currentPage', 1);
      this.$emit('update:itemsPerPage', value.id);
    },
    setPage(page) {
      this.$emit('update:currentPage', page);
      // TODO: this page-chage is deprecrated
      this.$emit('page-change', page);
    },
    nextPage() {
      if (this.currentPage < this.pageCount) {
        this.setPage(this.currentPage + 1);
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.setPage(this.currentPage - 1);
      }
    },
    isCurrent(page) {
      return page === this.currentPage;
    }
  },
  template: '#pagination-template'
};
</script>

<style lang="scss" scoped>
// -------------------------------------------------
// + Variables
// -------------------------------------------------

$space-between-item: 16px;
$border-radius: 4px;
$height: 36px;

// -------------------------------------------------
// + Main
// -------------------------------------------------

.al-pagination_container {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: spacing(4);
  margin-top: spacing(2);
}

/* Chrome, Safari, Edge, Opera */
.input-small::-webkit-outer-spin-button,
.input-small::-webkit-inner-spin-button {
  appearance: none;
  margin: 0;
}

.page-number {
  margin-bottom: -3px;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: -3px;
  width: 50px;

  .input-small {
    background: #f4f4f4;
    border: 0;
    border-radius: $border-radius;
    font-family: 'realistic', Helvetica;
    height: $height;

    &::placeholder {
      color: #b0b0b0;
    }
  }

  > .form-control:not(:last-child) {
    border-radius: $border-radius;
  }
}

/* Firefox */
.input-small[type='number'] {
  appearance: textfield;
}

.al-pagination {
  display: inline-flex;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    align-items: center;
    border-radius: 4px;
    display: flex;
    height: 37px;
    justify-content: center;
    margin: 0 2.5px;
    width: 30px;

    span,
    a {
      color: #36403f;
      font-family: 'realistic', Helvetica;
      font-size: 12.8px;
      font-weight: bold;
      height: 37px;
      letter-spacing: 0.2px;
      line-height: 36px;
      text-align: center;
      text-decoration: none;
      width: 30px;
    }

    &:first-child,
    &:last-child {
      span,
      a {
        font-size: 16px;
        position: relative;
      }
    }

    &.active,
    &:hover {
      background-color: #dff7ef;

      span,
      a {
        color: #12b981;
      }
    }

    &.disabled {
      span,
      a {
        color: #d9e3e2;
      }
    }

    &:hover {
      &:not(.active) {
        a {
          color: #12b981;
        }
      }
    }
  }
}
</style>
